import { onBeforeUnmount, onMounted } from "vue";

type ElementGetter = () => HTMLElement | null;

type UseAnchoredOverlayOptions = {
  getOverlayElement: ElementGetter;
  getAnchorElement: ElementGetter;
  viewportPadding?: number;
  verticalGap?: number;
};

function isVisible(el: HTMLElement) {
  const style = window.getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden") {
    return false;
  }

  return el.getClientRects().length > 0;
}

export function useAnchoredOverlay({
  getOverlayElement,
  getAnchorElement,
  viewportPadding = 8,
  verticalGap = 10
}: UseAnchoredOverlayOptions) {
  function syncNow() {
    const overlay = getOverlayElement();
    const anchor = getAnchorElement();
    if (!overlay || !anchor || !isVisible(overlay)) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();

    let left = anchorRect.left;
    const maxLeft = window.innerWidth - overlayRect.width - viewportPadding;
    if (left > maxLeft) {
      left = maxLeft;
    }
    if (left < viewportPadding) {
      left = viewportPadding;
    }

    let top = anchorRect.bottom + verticalGap;
    const maxTop = window.innerHeight - overlayRect.height - viewportPadding;
    if (top > maxTop) {
      top = Math.max(viewportPadding, maxTop);
    }

    overlay.style.position = "fixed";
    overlay.style.left = `${Math.round(left)}px`;
    overlay.style.top = `${Math.round(top)}px`;
  }

  function syncIfOpen() {
    const overlay = getOverlayElement();
    if (!overlay || !isVisible(overlay)) {
      return;
    }

    syncNow();
  }

  function scheduleSync() {
    setTimeout(syncIfOpen, 0);
    setTimeout(syncIfOpen, 60);
  }

  onMounted(() => {
    window.addEventListener("scroll", syncIfOpen, true);
    document.addEventListener("scroll", syncIfOpen, true);
    window.addEventListener("resize", syncIfOpen, { passive: true });
  });

  onBeforeUnmount(() => {
    window.removeEventListener("scroll", syncIfOpen, true);
    document.removeEventListener("scroll", syncIfOpen, true);
    window.removeEventListener("resize", syncIfOpen);
  });

  return {
    scheduleSync,
    syncIfOpen
  };
}
